import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { Camera, Eye, Trash2, Copy, ExternalLink, Mic, MicOff, Loader2, PlayCircle, Code2 } from "lucide-react";
import { toast } from "sonner";
import WalkthroughRecorder from "@/components/WalkthroughRecorder";
import WalkthroughAnalytics from "@/components/WalkthroughAnalytics";

const fmtDate = (iso) => new Date(iso).toLocaleString();
const fmtSize = (b) => b >= 1_048_576 ? `${(b/1_048_576).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;
const fmtSec = (s) => {
  const sec = Math.round(s || 0);
  const m = Math.floor(sec / 60);
  return m > 0 ? `${m}m ${sec % 60}s` : `${sec}s`;
};

function shareUrlFor(rec) {
  const root = window.location.origin;
  return `${root}/share/walkthrough/${rec.id}?k=${encodeURIComponent(rec.share_token)}`;
}

function embedCodeFor(rec) {
  const root = window.location.origin;
  const src = `${root}/embed/walkthrough/${rec.id}?k=${encodeURIComponent(rec.share_token)}`;
  return `<iframe src="${src}" width="100%" height="540" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" style="border-radius:8px;border:1px solid #1a1a1a"></iframe>`;
}

export default function Walkthroughs() {
  const [items, setItems] = useState(null);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/walkthroughs");
      setItems(r.data.items);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    api.get("/walkthroughs/_meta/eligibility")
      .then((r) => setEligibility(r.data))
      .catch(() => setEligibility({ allowed: false, reason: "unknown" }));
  }, []);

  const copyLink = async (rec) => {
    try { await navigator.clipboard.writeText(shareUrlFor(rec)); toast.success("Link copied."); }
    catch { toast.error("Couldn't copy — long-press the link to copy manually."); }
  };

  const copyEmbed = async (rec) => {
    try { await navigator.clipboard.writeText(embedCodeFor(rec)); toast.success("Embed code copied — paste it into any web page."); }
    catch { toast.error("Couldn't copy — select the snippet manually and copy."); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete "${rec.title}"? Anyone with the share link will lose access.`)) return;
    try {
      await api.delete(`/walkthroughs/${rec.id}`);
      toast.success("Walkthrough removed.");
      if (activeId === rec.id) setActiveId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const totalViews = (items || []).reduce((sum, it) => sum + (it.view_count || 0), 0);
  const totalDuration = (items || []).reduce((sum, it) => sum + (it.duration_s || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="walkthroughs-page">
      {/* header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · WALKTHROUGHS</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1">// WALKTHROUGH LIBRARY</h1>
          <div className="font-mono text-[11px] text-zinc-500 mt-1">
            Self-recorded screencasts of your DARA sessions. Share with the board, the CFO, or your customers.
          </div>
        </div>
        {eligibility?.allowed && (
          <button
            onClick={() => setRecorderOpen(true)}
            data-testid="library-record-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:bg-[#E59E00] flex items-center gap-2"
          >
            <Camera className="w-3.5 h-3.5" /> Record New
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Panel title="// RECORDINGS" testId="library-stat-count">
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">TOTAL</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#FFB000]">{items === null ? "—" : items.length}</div>
          </div>
        </Panel>
        <Panel title="// TOTAL VIEWS" testId="library-stat-views">
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">SHARED-LINK PLAYS</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#00E676]">{totalViews}</div>
          </div>
        </Panel>
        <Panel title="// TOTAL FOOTAGE" testId="library-stat-duration">
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">DURATION</div>
            <div className="font-heading font-black text-3xl mt-1">{fmtSec(totalDuration)}</div>
          </div>
        </Panel>
      </div>

      {/* eligibility note */}
      {eligibility && !eligibility.allowed && (
        <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 p-4 font-mono text-[11px] text-[#FFB000]">
          ⚠ Your seat can&apos;t record new walkthroughs. {eligibility.reason === "plan_required"
            ? `Upgrade to ${(eligibility.required_plans || []).join("/")} to enable this for Executive seats.`
            : "Workspace owners and Executive seats on Scale+ plans can record."}
        </div>
      )}

      {/* table */}
      <Panel title="// LIBRARY" testId="library-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-900">
                {["Title", "Recorded", "Length", "Audio", "Size", "Views", "Last viewed", "Actions"].map((h) => (
                  <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items === null && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              )}
              {items && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-500" data-testid="library-empty">
                  <div className="font-mono text-[11px] uppercase tracking-widest mb-2">No walkthroughs yet.</div>
                  <div className="text-xs text-zinc-600">Record a 60-180s screencast and share the link with your team.</div>
                </td></tr>
              )}
              {items && items.map((rec) => (
                <React.Fragment key={rec.id}>
                  <tr
                    className={`border-b border-zinc-900 hover:bg-zinc-900/30 cursor-pointer ${activeId === rec.id ? 'bg-zinc-900/40' : ''}`}
                    onClick={() => setActiveId(activeId === rec.id ? null : rec.id)}
                    data-testid={`library-row-${rec.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-heading font-bold text-white truncate max-w-[280px]">{rec.title}</div>
                      <div className="font-mono text-[10px] text-zinc-600 truncate max-w-[280px]">{rec.id}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-zinc-400">{fmtDate(rec.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-[11px]">{fmtSec(rec.duration_s)}</td>
                    <td className="px-4 py-3">{rec.has_audio ? <Mic className="w-3.5 h-3.5 text-[#00E676]" /> : <MicOff className="w-3.5 h-3.5 text-zinc-700" />}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-zinc-400">{fmtSize(rec.size_bytes)}</td>
                    <td className="px-4 py-3 font-mono text-[11px]">
                      <span className={rec.view_count > 0 ? "text-[#FFB000]" : "text-zinc-600"}>
                        <Eye className="w-3 h-3 inline mr-1" />{rec.view_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-zinc-500">
                      {rec.last_viewed_at ? fmtDate(rec.last_viewed_at) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => copyLink(rec)}
                          data-testid={`library-copy-${rec.id}`}
                          className="p-1.5 text-zinc-500 hover:text-[#FFB000]"
                          title="Copy share link"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <a
                          href={shareUrlFor(rec)}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`library-open-${rec.id}`}
                          className="p-1.5 text-zinc-500 hover:text-[#00E676]"
                          title="Open share page"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <button
                          onClick={() => remove(rec)}
                          data-testid={`library-delete-${rec.id}`}
                          className="p-1.5 text-zinc-500 hover:text-[#FF3B30]"
                          title="Delete walkthrough"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {activeId === rec.id && (
                    <tr className="bg-black/40 border-b border-zinc-900">
                      <td colSpan={8} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-2">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-2 flex items-center gap-1.5">
                              <PlayCircle className="w-3.5 h-3.5" /> INLINE PREVIEW
                            </div>
                            <video
                              key={rec.id}
                              controls
                              src={`${API}/walkthroughs/${rec.id}/video?k=${encodeURIComponent(rec.share_token)}`}
                              className="w-full bg-zinc-950 border border-zinc-900"
                              data-testid={`library-video-${rec.id}`}
                            />
                          </div>
                          <div className="space-y-3">
                            <div>
                              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SHARE URL</div>
                              <div className="font-mono text-[10px] break-all text-zinc-300 mt-1 p-2 bg-zinc-950 border border-zinc-900">
                                {shareUrlFor(rec)}
                              </div>
                              <div className="flex gap-2 mt-2">
                                <button onClick={() => copyLink(rec)} className="border border-zinc-800 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-900 flex items-center gap-1.5">
                                  <Copy className="w-3 h-3" /> COPY
                                </button>
                                <a href={shareUrlFor(rec)} target="_blank" rel="noopener noreferrer" className="border border-zinc-800 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-900 flex items-center gap-1.5">
                                  <ExternalLink className="w-3 h-3" /> OPEN
                                </a>
                              </div>
                            </div>
                            <div className="border-t border-zinc-900 pt-3">
                              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                                <Code2 className="w-3 h-3" /> EMBED CODE
                              </div>
                              <div className="font-mono text-[9px] break-all text-zinc-400 mt-1 p-2 bg-zinc-950 border border-zinc-900 max-h-24 overflow-y-auto" data-testid={`library-embed-code-${rec.id}`}>
                                {embedCodeFor(rec)}
                              </div>
                              <button
                                onClick={() => copyEmbed(rec)}
                                data-testid={`library-embed-copy-${rec.id}`}
                                className="mt-2 border border-[#FFB000]/40 text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FFB000]/10 flex items-center gap-1.5"
                              >
                                <Copy className="w-3 h-3" /> COPY EMBED CODE
                              </button>
                              <div className="font-mono text-[10px] text-zinc-600 mt-2 leading-relaxed">
                                Paste into your ABM landing page, Notion doc, or post-meeting email.
                                Watch %, region, and re-watches feed back into your Analytics panel.
                              </div>
                            </div>
                            <div className="border-t border-zinc-900 pt-2">
                              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SALES SIGNAL</div>
                              {rec.view_count === 0 ? (
                                <div className="font-mono text-[11px] text-zinc-500 mt-1">
                                  No external plays yet — link not shared or unopened.
                                </div>
                              ) : rec.view_count <= 3 ? (
                                <div className="font-mono text-[11px] text-[#FFB000] mt-1">
                                  ⚡ {rec.view_count} play{rec.view_count === 1 ? "" : "s"} — early traction. Follow up within 48h.
                                </div>
                              ) : (
                                <div className="font-mono text-[11px] text-[#00E676] mt-1">
                                  🔥 {rec.view_count} plays — high re-watch behaviour. Treat as active opportunity.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-5 border-t border-zinc-900 pt-4">
                          <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-3">// VIEWER ANALYTICS</div>
                          <WalkthroughAnalytics recordingId={rec.id} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <WalkthroughRecorder
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onComplete={() => { setRecorderOpen(false); load(); }}
      />
    </div>
  );
}
